interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
}

export function Logo({ className = '', size = 'md', showText = true }: LogoProps) {
  const sizes = {
    sm: { icon: 28, text: 'text-base' },
    md: { icon: 36, text: 'text-lg' },
    lg: { icon: 48, text: 'text-2xl' },
  };

  const { icon, text } = sizes[size];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Custom shield/vault logo */}
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
      >
        {/* Outer shield shape */}
        <path
          d="M24 4L6 12V22C6 33.05 13.68 43.22 24 46C34.32 43.22 42 33.05 42 22V12L24 4Z"
          stroke="url(#logoGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Inner vault circle */}
        <circle
          cx="24"
          cy="24"
          r="10"
          stroke="url(#logoGradient)"
          strokeWidth="1.5"
          fill="none"
        />

        {/* Vault door details - horizontal line */}
        <line
          x1="17"
          y1="24"
          x2="31"
          y2="24"
          stroke="url(#logoGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* Vault door details - vertical line */}
        <line
          x1="24"
          y1="17"
          x2="24"
          y2="31"
          stroke="url(#logoGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* Center dot */}
        <circle
          cx="24"
          cy="24"
          r="2.5"
          fill="url(#logoGradient)"
        />

        {/* Corner accents */}
        <circle cx="24" cy="14" r="1.5" fill="url(#logoGradient)" opacity="0.6" />
        <circle cx="24" cy="34" r="1.5" fill="url(#logoGradient)" opacity="0.6" />
        <circle cx="14" cy="24" r="1.5" fill="url(#logoGradient)" opacity="0.6" />
        <circle cx="34" cy="24" r="1.5" fill="url(#logoGradient)" opacity="0.6" />

        <defs>
          <linearGradient id="logoGradient" x1="6" y1="4" x2="42" y2="46" gradientUnits="userSpaceOnUse">
            <stop stopColor="#E8CD73" />
            <stop offset="0.5" stopColor="#D4AF37" />
            <stop offset="1" stopColor="#9A7B2D" />
          </linearGradient>
        </defs>
      </svg>

      {showText && (
        <span className={`font-serif ${text} tracking-tight`}>
          <span style={{ color: '#fcfaf5' }}>Secure</span>
          <span style={{ color: '#d4af37' }}>Send</span>
          <span style={{ color: '#9c9991' }}>.pro</span>
        </span>
      )}
    </div>
  );
}

// Icon-only version for favicon/small spaces
export function LogoIcon({ size = 32, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M24 4L6 12V22C6 33.05 13.68 43.22 24 46C34.32 43.22 42 33.05 42 22V12L24 4Z"
        stroke="url(#logoIconGradient)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle
        cx="24"
        cy="24"
        r="10"
        stroke="url(#logoIconGradient)"
        strokeWidth="1.5"
        fill="none"
      />
      <line
        x1="17"
        y1="24"
        x2="31"
        y2="24"
        stroke="url(#logoIconGradient)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="24"
        y1="17"
        x2="24"
        y2="31"
        stroke="url(#logoIconGradient)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="24" cy="24" r="2.5" fill="url(#logoIconGradient)" />
      <circle cx="24" cy="14" r="1.5" fill="url(#logoIconGradient)" opacity="0.6" />
      <circle cx="24" cy="34" r="1.5" fill="url(#logoIconGradient)" opacity="0.6" />
      <circle cx="14" cy="24" r="1.5" fill="url(#logoIconGradient)" opacity="0.6" />
      <circle cx="34" cy="24" r="1.5" fill="url(#logoIconGradient)" opacity="0.6" />
      <defs>
        <linearGradient id="logoIconGradient" x1="6" y1="4" x2="42" y2="46" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E8CD73" />
          <stop offset="0.5" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#9A7B2D" />
        </linearGradient>
      </defs>
    </svg>
  );
}
